import { useEffect, useMemo, useState } from 'react';
import { apiJson } from '../apiClient';
import { readOverviewBranchFilter } from '../components/OverviewCommandPanel';
import { normalizeRole } from '../utils/userLocale';

/**
 * Resolves branch for stock/orders: owner picks from list; staff use assigned branch.
 */
export default function useBranchAssignment(user) {
  const isOwner = normalizeRole(user?.role) === 'owner';
  const staffBranchId = user?.branch?.id != null ? String(user.branch.id) : '';
  const staffBranchName = user?.branch?.name || '';

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(() => (isOwner ? readOverviewBranchFilter() : staffBranchId));
  const [loading, setLoading] = useState(isOwner);

  useEffect(() => {
    if (!isOwner) {
      setBranchId(staffBranchId);
      setLoading(false);
      return undefined;
    }

    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const data = await apiJson('/owner/branches');
        if (!mounted) return;
        const list = (data.branches || []).filter((b) => b.is_active !== false);
        setBranches(list);
        const preset = readOverviewBranchFilter();
        if (preset && list.some((b) => String(b.id) === preset)) {
          setBranchId(preset);
        } else if (list.length) {
          setBranchId(String(list[0].id));
        } else {
          setBranchId('');
        }
      } catch {
        if (!mounted) return;
        setBranches([]);
        setBranchId('');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isOwner, staffBranchId]);

  const resolvedBranchId = isOwner ? branchId : staffBranchId;
  const ready = Boolean(resolvedBranchId);
  const selectedBranchName = useMemo(() => {
    if (!isOwner) return staffBranchName;
    const hit = branches.find((b) => String(b.id) === String(branchId));
    return hit?.name || '';
  }, [isOwner, staffBranchName, branches, branchId]);

  return {
    isOwner,
    branches,
    branchId: resolvedBranchId,
    setBranchId,
    loading,
    ready,
    selectedBranchName,
    staffBranchName,
  };
}
